var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useMemo, useReducer } from 'react';
import { useAsyncEffect } from '@design-stack-ct/utility-react';
const reducer = (state, action) => {
    switch (action.type) {
        case 'FETCHING':
            return {
                status: 'loading',
                refreshing: state.refreshing,
            };
        case 'FETCHING_MORE':
            return {
                status: 'loading-more',
                refreshing: false,
            };
        case 'ERROR':
            return Object.assign(Object.assign({}, state), { status: 'error', error: action.payload, refreshing: false });
        case 'FETCH_SUCCESS':
            return {
                status: 'ready',
                refreshing: false,
            };
        case 'REFRESH':
            return Object.assign(Object.assign({}, state), { refreshing: true });
        default:
            return state;
    }
};
// fetchAssets can only error out on a network error
// if it were to error out, it would still return an empty list
// if a fetchLimit of 0 is provided, every asset associated with the assetStore is fetched
export const fetchSignedAssets = ({ assetStore, fetchLimit = 20, folderName }) => __awaiter(void 0, void 0, void 0, function* () {
    const fetchedSignedAssets = yield assetStore.fetchAssets({
        limit: fetchLimit,
        folderName,
    });
    assetStore.assets.forEach((asset) => asset.presign());
    return fetchedSignedAssets;
});
/**
 * Hook that returns a state object.
 * This object indicates the upload status, and depending on the status also exposes if the assetStore has more to fetch,
 * and a function to allow the user to fetch more
 */
export const useUploadedAssets = (params) => {
    const { fetchLimit, assetStore, onError, folderName } = params;
    const [state, dispatch] = useReducer(reducer, {
        status: 'idle',
        refreshing: false,
    });
    useAsyncEffect(({ runIfMounted }) => {
        const executeFetchSignedAssets = () => __awaiter(void 0, void 0, void 0, function* () {
            if (!assetStore) {
                return;
            }
            try {
                dispatch({ type: 'FETCHING' });
                yield fetchSignedAssets({
                    assetStore,
                    // Suggested to select a fetch limit that is divisible by 2 and 3 - it will look better.
                    fetchLimit: fetchLimit !== undefined ? fetchLimit : 30,
                    folderName,
                });
                runIfMounted(() => __awaiter(void 0, void 0, void 0, function* () {
                    dispatch({
                        type: 'FETCH_SUCCESS',
                    });
                }));
            }
            catch (error) {
                if (error instanceof Error && error.name === 'AbortError') {
                    return;
                }
                if (error instanceof Error) {
                    dispatch({ type: 'ERROR', payload: error });
                }
            }
        });
        executeFetchSignedAssets();
    }, [assetStore, fetchLimit, folderName, state.refreshing]);
    const refreshAssets = useCallback(() => {
        dispatch({ type: 'REFRESH' });
    }, [dispatch]);
    const fetchMore = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            dispatch({ type: 'FETCHING_MORE' });
            yield assetStore.fetchMore();
            dispatch({
                type: 'FETCH_SUCCESS',
            });
        }
        catch (err) {
            if (err instanceof Error) {
                dispatch({ type: 'ERROR', payload: err });
            }
            const error = Error(`Failed to fetch more assets: ${err}`);
            if (onError) {
                onError(error);
            }
            else {
                throw error;
            }
        }
    }), [assetStore, onError]);
    return useMemo(() => {
        if (state.status === 'ready') {
            return Object.assign(Object.assign({}, state), { fetchMore, refreshAssets, canFetchMore: assetStore.canFetchMore });
        }
        if (state.status === 'loading-more') {
            return Object.assign(Object.assign({}, state), { canFetchMore: assetStore.canFetchMore });
        }
        return state;
    }, [state, assetStore.canFetchMore, fetchMore, refreshAssets]);
};
//# sourceMappingURL=useUploadedAssets.js.map